import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { computePlacement } from "./guidePlacement";
import "./chartGuide.css";
import { TUTORIALS, useTutorialMeasurement } from "../../utils/tutorialMeasurement";

const getInitialIsMobile = () =>
  typeof window !== "undefined" ? window.innerWidth <= 768 : false;

const MOBILE_FORCED_BELOW_STEP_IDS = [1, 3, 4];

function computeMobileBelowPlacement({ rect, bubbleWidth, bubbleHeight, bottomNavbarHeight, pointerAtX }) {
  const margin = 12;
  const gap = 24;
  const maxTop = window.innerHeight - bottomNavbarHeight - bubbleHeight - margin;
  const maxLeft = window.innerWidth - bubbleWidth - margin;

  const top = Math.min(Math.max(rect.bottom + gap, margin), Math.max(maxTop, margin));

  const centeredLeft = rect.left + rect.width / 2 - bubbleWidth / 2;
  const left = Math.min(Math.max(centeredLeft, margin), Math.max(maxLeft, margin));

  const ARROW_SIZE = 20;
  const targetCenter = pointerAtX != null ? pointerAtX : rect.left + rect.width / 2;
  const rawPercent = ((targetCenter - left) / bubbleWidth) * 100;
  const limit = (ARROW_SIZE / bubbleWidth) * 100 + 4;
  const arrowOffset = `${Math.min(Math.max(rawPercent, limit), 100 - limit)}%`;

  return { top, left, arrow: "top", arrowOffset };
}

const ProfileGuideSteps = ({ setShowGuide, shouldPersistSetting, onGuideComplete }) => {
  const [isMobileView, setIsMobileView] = useState(getInitialIsMobile);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (getInitialIsMobile()) {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, []);

  const guideSteps = useMemo(
    () => [
      {
        id: 1,
        target: "profile-guide-edit-view",
        mobileTarget: "profile-guide-edit-view-mobile",
        text: "Make your profile yours. Add your trader details and bio, then tap View as public to see what other traders see.",
      },
      {
        id: 2,
        target: "profile-guide-record",
        mobileTarget: "profile-guide-record-mobile",
        text: "This is your verified QUSHi record. Every trade you make builds the numbers here: return, drawdown, win rate, profit factor, and more.",
      },
      {
        id: 3,
        target: "profile-guide-coins",
        mobileTarget: "profile-guide-coins-mobile",
        text: "Keep collecting. More ways to use QUSHi Coins are planned as QUSHi grows.",
      },
      {
        id: 4,
        target: "profile-guide-tabs",
        mobileTarget: "profile-guide-tabs-mobile",
        text: "Go deeper than the headline numbers. Browse posts, holdings, trade history, and performance to see the full picture behind any trader's record.",
      },
    ],
    []
  );

  const computeVisibleSteps = useCallback(
    (mobile) =>
      guideSteps.filter((step) => {
        const targetId = mobile && step.mobileTarget ? step.mobileTarget : step.target;
        const element = targetId ? document.getElementById(targetId) : null;
        return element && element.offsetParent !== null;
      }),
    [guideSteps]
  );

  const [activeSteps, setActiveSteps] = useState(() =>
    computeVisibleSteps(getInitialIsMobile())
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [bubblePosition, setBubblePosition] = useState({ top: -1000, left: -1000 });
  const [arrowPosition, setArrowPosition] = useState("bottom");
  const [arrowOffset, setArrowOffset] = useState("50%");
  const [isPositioned, setIsPositioned] = useState(false);
  const bubbleRef = useRef(null);
  const current = activeSteps[stepIndex];

  const finalizeGuide = useCallback(async () => {
    if (typeof onGuideComplete === "function") {
      await onGuideComplete(shouldPersistSetting);
    }
  }, [onGuideComplete, shouldPersistSetting]);

  const resolveTargetId = useCallback(
    (step) => {
      if (!step) {
        return null;
      }
      return isMobileView && step.mobileTarget ? step.mobileTarget : step.target;
    },
    [isMobileView]
  );

  useEffect(() => {
    const updateActiveSteps = () => {
      const filteredSteps = computeVisibleSteps(isMobileView);

      if (!filteredSteps.length) {
        setActiveSteps([]);
        setStepIndex(0);
        finalizeGuide();
        setShowGuide(false);
        return;
      }

      setActiveSteps(filteredSteps);
      setStepIndex((prev) => Math.min(prev, filteredSteps.length - 1));
    };

    updateActiveSteps();
    window.addEventListener("resize", updateActiveSteps);
    return () => window.removeEventListener("resize", updateActiveSteps);
  }, [computeVisibleSteps, isMobileView, setShowGuide, finalizeGuide]);

  useEffect(() => {
    if (!current) {
      return undefined;
    }

    const highlighted = document.querySelectorAll(".chart-guide-highlight");
    highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));

    const targetId = resolveTargetId(current);
    const targetElement = targetId ? document.getElementById(targetId) : null;
    if (targetElement) {
      targetElement.classList.add("chart-guide-highlight");
    }

    return () => {
      const cleanupElement = targetId ? document.getElementById(targetId) : null;
      if (cleanupElement) {
        cleanupElement.classList.remove("chart-guide-highlight");
      }
    };
  }, [current, resolveTargetId]);

  useEffect(() => {
    if (!current) {
      return undefined;
    }

    const isMobile = isMobileView;
    const bottomNavbarHeight = isMobile ? 64 : 0;

    const compute = () => {
      const targetId = resolveTargetId(current);
      const targetElement = targetId ? document.getElementById(targetId) : null;
      if (!targetElement) {
        setIsPositioned(false);
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      const bubbleElement = bubbleRef.current;
      const fallbackWidth = isMobile ? 280 : 320;
      const fallbackHeight = isMobile ? 200 : 180;
      const bubbleWidth = bubbleElement?.offsetWidth || fallbackWidth;
      const bubbleHeight = bubbleElement?.offsetHeight || fallbackHeight;

      const useForcedBelow =  (isMobile && MOBILE_FORCED_BELOW_STEP_IDS.includes(current.id)) ||
        (!isMobile && current.id === 3);

      let pointerAtX = null;
      if (current.id === 1 && targetElement.children && targetElement.children.length >= 2) {
        const firstRect = targetElement.children[0].getBoundingClientRect();
        const secondRect = targetElement.children[1].getBoundingClientRect();
        pointerAtX = (firstRect.right + secondRect.left) / 2;
      }

      const placed = useForcedBelow
        ? computeMobileBelowPlacement({ rect, bubbleWidth, bubbleHeight, bottomNavbarHeight, pointerAtX })
        : computePlacement({
            targetRect: rect,
            bubbleWidth,
            bubbleHeight,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
            bottomInset: bottomNavbarHeight,
          });
      const { left, arrow, arrowOffset } = placed;
      // Desktop steps 1, 2, 3, and 4 only: nudge down 12px per client request.
      const top = !isMobile ? placed.top + 12 : placed.top;

      setBubblePosition({ top, left });
      setArrowPosition(arrow);
      setArrowOffset(arrowOffset);
      setIsPositioned(true);
    };

    let settleTimer;
    const scrollAndPosition = () => {
      const targetId = resolveTargetId(current);
      const targetElement = targetId ? document.getElementById(targetId) : null;
      if (!targetElement) {
        setIsPositioned(false);
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      const needsExtraBuffer = (isMobile && (current.id === 3 || current.id === 4)) ||
        (!isMobile && current.id === 3);
      const bottomBuffer = needsExtraBuffer ? 260 : 120;
      const comfortablyVisible =
        rect.top > 80 && rect.bottom < window.innerHeight - bottomNavbarHeight - bottomBuffer;
      if (!comfortablyVisible) {
        targetElement.scrollIntoView({
          behavior: "auto",
          block: needsExtraBuffer ? "center" : "nearest",
          inline: "nearest",
        });

        if (needsExtraBuffer) {
          const settled = targetElement.getBoundingClientRect();
          const overshoot =
            settled.bottom - (window.innerHeight - bottomNavbarHeight - bottomBuffer);
          if (overshoot > 0) {
            window.scrollBy(0, overshoot);
          }
        }
      }

      compute();
      settleTimer = setTimeout(compute, isMobile ? 150 : 220);
    };

    const timer = setTimeout(scrollAndPosition, 60);
    window.addEventListener("resize", compute, { passive: true });
    window.addEventListener("scroll", compute, { passive: true });

    return () => {
      clearTimeout(timer);
      clearTimeout(settleTimer);
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute);
    };
  }, [current, isMobileView, resolveTargetId]);

  // Measurement Core §6.4 tutorial events.
  const tutorial = useTutorialMeasurement({
    tutorial: TUTORIALS.profile,
    totalSteps: activeSteps.length,
    stepIndex,
    visible: isPositioned,
  });

  const handleNext = async () => {
    if (stepIndex < activeSteps.length - 1) {
      setIsPositioned(false);
      setBubblePosition({ top: -1000, left: -1000 });
      setStepIndex(stepIndex + 1);
      return;
    }
    tutorial.complete();

    try {
      await finalizeGuide();
    } finally {
      setShowGuide(false);
    }
  };

  const handleSkip = async () => {
    tutorial.skip();
    const highlighted = document.querySelectorAll(".chart-guide-highlight");
    highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));
    try {
      await finalizeGuide();
    } finally {
      setShowGuide(false);
    }
  };

  useEffect(() => {
    return () => {
      const highlighted = document.querySelectorAll(".chart-guide-highlight");
      highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));
    };
  }, []);

  if (!current) {
    return null;
  }

  const displayText = isMobileView && current.mobileText ? current.mobileText : current.text;

  return (
    <>
      <div className="chart-guide-backdrop" onClick={handleSkip} />

      <div
        ref={bubbleRef}
        className={`chart-guide-bubble dynamic-position arrow-${arrowPosition} step-${stepIndex + 1} profile-guide-bubble`}
        style={{
          top: bubblePosition.top,
          left: bubblePosition.left,
          opacity: isPositioned ? 1 : 0,
          "--arrow-offset": arrowOffset,
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        <div className="guide-step-indicator">
          Step {stepIndex + 1} of {activeSteps.length}
        </div>
        <div className="guide-text">{displayText}</div>

        <div className="guide-buttons">
          <button className="next-btn" onClick={handleNext}>
            {stepIndex === activeSteps.length - 1 ? "Finish" : "Next"}
          </button>
          {/* Measurement Core §6.4: the final step shows Finish only. */}
          {stepIndex < activeSteps.length - 1 && (
            <button className="skip-btn" onClick={handleSkip}>
              Skip
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfileGuideSteps;
